import { useEffect, useState } from 'react';
import { FounderNav } from '../components/FounderNav';
import { emailOtp, signIn } from '../lib/auth';
import { isNative } from '../lib/capacitor';
import { alertDialog, promptDialog } from '../lib/dialog';
import {
  createCommunity,
  fetchFounderCommunities,
  fetchFounderDemand,
  fetchFounderOverview,
  fetchModerationStats,
  fetchReadiness,
  fetchUnmatched,
  pairConciergely,
  type CreateCommunityInput,
  type DemandRow,
  type FounderCommunity,
  type FounderOverview,
  type ModerationStats,
  type ReadinessReport,
  type UnmatchedMember,
} from '../lib/api';

/**
 * Founder super-admin dashboard. Cross-community read-only metrics, with
 * step-up auth: a stale (>30 min) session forces a re-sign-in.
 */
export function Founder() {
  const [overview, setOverview] = useState<FounderOverview | null>(null);
  const [communities, setCommunities] = useState<FounderCommunity[] | null>(null);
  const [readiness, setReadiness] = useState<ReadinessReport | null>(null);
  const [demand, setDemand] = useState<DemandRow[] | null>(null);
  const [stale, setStale] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Concierge pairing state.
  const [conciergeCommunityId, setConciergeCommunityId] = useState<string>('');
  const [unmatched, setUnmatched] = useState<UnmatchedMember[] | null>(null);
  const [pickedA, setPickedA] = useState<string>('');
  const [pickedB, setPickedB] = useState<string>('');
  const [conciergeBusy, setConciergeBusy] = useState(false);
  const [conciergeMsg, setConciergeMsg] = useState<string | null>(null);
  // Moderation analytics widget.
  const [modStats, setModStats] = useState<ModerationStats | null>(null);
  // New-community form state. Open is lazy — most loads of /founder
  // don't need the form, so we toggle it instead of rendering inline.
  const [newCommOpen, setNewCommOpen] = useState(false);
  const [newComm, setNewComm] = useState<CreateCommunityInput>({
    name: '',
    slug: '',
    type: 'university',
    emailDomains: [],
    crisisEscalationName: '',
    crisisEscalationContact: '',
    crisisEscalationType: 'campus_office',
    crisisResourceNote: '',
  });
  const [newCommDomainsRaw, setNewCommDomainsRaw] = useState('');
  const [newCommBusy, setNewCommBusy] = useState(false);
  const [newCommMsg, setNewCommMsg] = useState<string | null>(null);

  useEffect(() => {
    void Promise.all([fetchFounderOverview(), fetchFounderCommunities()]).then(([o, c]) => {
      if (!o.ok && o.stale) {
        setStale(true);
        return;
      }
      if (!o.ok) {
        setError(o.error);
        return;
      }
      setOverview(o.data);
      if (c.ok) setCommunities(c.data.communities);
    });
    // The readiness check is unauthenticated server-side, but we still want
    // it scoped to the founder UI — render it on the same page so a single
    // dashboard load gives the operator the green-light list.
    void fetchReadiness()
      .then(setReadiness)
      .catch(() => setReadiness(null));
    // Demand: outreach_lead rows grouped by domain + institution. The
    // partnerships prospect list — who's been knocking on the door.
    void fetchFounderDemand()
      .then(setDemand)
      .catch(() => setDemand(null));
    void fetchModerationStats()
      .then(setModStats)
      .catch(() => setModStats(null));
  }, []);

  async function submitNewCommunity() {
    if (newCommBusy) return;
    const domains = newCommDomainsRaw
      .split(/[\s,]+/)
      .map((d) => d.trim())
      .filter(Boolean);
    if (!newComm.name || !newComm.slug || domains.length === 0) {
      setNewCommMsg('Name, slug, and at least one email domain are required.');
      return;
    }
    if (!newComm.crisisEscalationName || !newComm.crisisEscalationContact) {
      setNewCommMsg('Crisis escalation contact is required (a hard launch blocker).');
      return;
    }
    setNewCommBusy(true);
    setNewCommMsg(null);
    const result = await createCommunity({ ...newComm, emailDomains: domains });
    setNewCommBusy(false);
    if (result.ok) {
      setNewCommMsg(
        result.alreadyExists
          ? `Slug "${newComm.slug}" was already a community. No changes made.`
          : `Created "${newComm.name}". Reload to see it in the list below.`,
      );
      setNewComm({
        name: '',
        slug: '',
        type: 'university',
        emailDomains: [],
        crisisEscalationName: '',
        crisisEscalationContact: '',
        crisisEscalationType: 'campus_office',
        crisisResourceNote: '',
      });
      setNewCommDomainsRaw('');
      const c = await fetchFounderCommunities();
      if (c.ok) setCommunities(c.data.communities);
    } else {
      setNewCommMsg(result.error);
    }
  }

  async function loadUnmatched(communityId: string) {
    setConciergeCommunityId(communityId);
    setPickedA('');
    setPickedB('');
    setConciergeMsg(null);
    if (!communityId) {
      setUnmatched(null);
      return;
    }
    const rows = await fetchUnmatched(communityId);
    setUnmatched(rows);
  }

  async function pairThem() {
    if (!conciergeCommunityId || !pickedA || !pickedB || pickedA === pickedB) return;
    setConciergeBusy(true);
    setConciergeMsg(null);
    const result = await pairConciergely(conciergeCommunityId, pickedA, pickedB);
    setConciergeBusy(false);
    if (result.ok) {
      setConciergeMsg('Paired. They can talk now.');
      setPickedA('');
      setPickedB('');
      const rows = await fetchUnmatched(conciergeCommunityId);
      setUnmatched(rows);
    } else {
      setConciergeMsg(result.error);
    }
  }

  async function reauth() {
    // The user is already signed in; mint a fresh session so the >30-min
    // step-up gate clears. On native (Capacitor) a magic LINK can't be
    // used — Gmail opens it in Chrome and the cookie lands in the wrong
    // jar (the same reason sign-in uses an OTP code on mobile). So on
    // native we send a 6-digit code and verify it in-app; on web we keep
    // the one-tap magic link.
    if (isNative()) {
      const email = await promptDialog({
        title: 'Fresh sign-in required',
        message:
          'Founder tools require a recent sign-in (last 30 minutes). Enter your founder email and we will text you a 6-digit code.',
        inputLabel: 'Founder email',
        placeholder: 'you@yourdomain.com',
        requireValue: true,
        confirmLabel: 'Send code',
      });
      if (!email) return;
      const sent = await emailOtp.sendVerificationOtp({ email: email.trim(), type: 'sign-in' });
      if (sent.error) {
        await alertDialog({
          title: "Couldn't send the code",
          message: sent.error.message ?? 'Please check the email address and try again.',
        });
        return;
      }
      const code = await promptDialog({
        title: 'Enter your code',
        message: `We sent a 6-digit code to ${email.trim()}. It expires in 10 minutes.`,
        inputLabel: '6-digit code',
        placeholder: '123456',
        requireValue: true,
        confirmLabel: 'Verify',
      });
      if (!code) return;
      const res = await signIn.emailOtp({ email: email.trim(), otp: code.replace(/\D/g, '') });
      if (res.error) {
        await alertDialog({
          title: "That code didn't work",
          message: res.error.message ?? 'Check the digits or send a new code.',
        });
        return;
      }
      // Fresh session minted — hard-reload so the step-up gate re-reads it.
      window.location.assign('/founder');
      return;
    }

    // Web path: one-tap magic link.
    const email = await promptDialog({
      title: 'Fresh sign-in required',
      message:
        'Founder writes require a recent sign-in (last 30 minutes). Enter your founder email and we will send a fresh magic link.',
      inputLabel: 'Founder email',
      placeholder: 'you@yourdomain.com',
      requireValue: true,
      confirmLabel: 'Send link',
    });
    if (!email) return;
    await signIn.magicLink({ email, name: email, callbackURL: '/founder' });
    await alertDialog({
      title: 'Check your inbox',
      message: 'A fresh sign-in link is on its way. It expires in 30 minutes.',
    });
  }

  if (stale) {
    return (
      <main className="wide">
        <div className="card">
          <h1>Founder tools</h1>
          <p>
            For your safety, founder tools require a recently-signed-in session. Sign in fresh to
            continue.
          </p>
          <div className="stack">
            <button type="button" onClick={() => void reauth()}>
              Re-sign in to access founder tools
            </button>
          </div>
        </div>
      </main>
    );
  }

  function ratio(value: number, max: number): string {
    if (!max) return '0%';
    return `${Math.round((value / max) * 100)}%`;
  }

  function fmtDuration(seconds: number | null): string {
    if (seconds == null) return '—';
    if (seconds < 60) return `${seconds.toFixed(0)}s`;
    if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
    return `${(seconds / 3600).toFixed(1)}h`;
  }

  // Operationally important checks the operator must act on before
  // accepting real students. The readiness widget below shows the full
  // list; this banner surfaces only the ones a missed status would
  // create a real-world problem on day one.
  const criticalReadinessGaps =
    readiness?.checks.filter(
      (c) =>
        c.status !== 'ok' &&
        ['email', 'ncmec_submission', 'auth_secret', 'scheduler'].includes(c.name),
    ) ?? [];

  const peakConvs = overview
    ? Math.max(
        overview.conversations.active,
        overview.conversations.graduated,
        overview.conversations.ended,
        overview.conversations.dead,
        1,
      )
    : 1;

  return (
    <main className="wide">
      <div className="card">
        <h1>Founder dashboard</h1>
        <p className="muted">Cross-community read-only view. Every load is audit-logged.</p>
        <FounderNav />
        {error && <p className="error">{error}</p>}
        {!overview && !error && <p className="muted">Loading…</p>}

        {criticalReadinessGaps.length > 0 && (
          <div className="founder-banner">
            <strong>Before opening sign-ups for a real cohort:</strong>
            <ul>
              {criticalReadinessGaps.map((c) => (
                <li key={c.name}>
                  <strong>{c.name.replace(/_/g, ' ')}</strong> — {c.detail}
                </li>
              ))}
            </ul>
          </div>
        )}

        {overview && (
          <>
            <section className="outcomes-section">
              <h2>Platform</h2>
              <ul className="bars">
                <li>
                  <span className="bars-label">Total signups</span>
                  <div className="bars-track">
                    <span
                      className="bars-fill"
                      style={{ width: '100%', background: 'var(--green)' }}
                    />
                  </div>
                  <span className="bars-value">{overview.totalSignups}</span>
                </li>
                <li>
                  <span className="bars-label">Weekly active</span>
                  <div className="bars-track">
                    <span
                      className="bars-fill"
                      style={{
                        width: ratio(overview.weeklyActive, overview.totalSignups || 1),
                        background: 'var(--green)',
                      }}
                    />
                  </div>
                  <span className="bars-value">{overview.weeklyActive}</span>
                </li>
                <li>
                  <span className="bars-label">Messages (7d)</span>
                  <div className="bars-track">
                    <span
                      className="bars-fill"
                      style={{ width: '100%', background: 'var(--green)' }}
                    />
                  </div>
                  <span className="bars-value">{overview.messagesLast7d}</span>
                </li>
                <li>
                  <span className="bars-label">Crisis flags (30d)</span>
                  <div className="bars-track">
                    <span
                      className="bars-fill"
                      style={{ width: '100%', background: 'var(--clay)' }}
                    />
                  </div>
                  <span className="bars-value">{overview.crisisLast30d}</span>
                </li>
                <li>
                  <span className="bars-label">Graduation rate</span>
                  <div className="bars-track">
                    <span
                      className="bars-fill"
                      style={{
                        width: ratio(overview.graduationRate, 1),
                        background: 'var(--green)',
                      }}
                    />
                  </div>
                  <span className="bars-value">{(overview.graduationRate * 100).toFixed(1)}%</span>
                </li>
              </ul>
            </section>

            <section className="outcomes-section">
              <h2>Conversations</h2>
              <ul className="bars">
                <li>
                  <span className="bars-label">Active</span>
                  <div className="bars-track">
                    <span
                      className="bars-fill"
                      style={{
                        width: ratio(overview.conversations.active, peakConvs),
                        background: 'var(--green)',
                      }}
                    />
                  </div>
                  <span className="bars-value">{overview.conversations.active}</span>
                </li>
                <li>
                  <span className="bars-label">Graduated</span>
                  <div className="bars-track">
                    <span
                      className="bars-fill"
                      style={{
                        width: ratio(overview.conversations.graduated, peakConvs),
                        background: 'var(--green)',
                      }}
                    />
                  </div>
                  <span className="bars-value">{overview.conversations.graduated}</span>
                </li>
                <li>
                  <span className="bars-label">Abandoned (ended)</span>
                  <div className="bars-track">
                    <span
                      className="bars-fill"
                      style={{
                        width: ratio(overview.conversations.ended, peakConvs),
                        background: 'var(--clay)',
                      }}
                    />
                  </div>
                  <span className="bars-value">{overview.conversations.ended}</span>
                </li>
                <li>
                  <span className="bars-label">Dead (never started)</span>
                  <div className="bars-track">
                    <span
                      className="bars-fill"
                      style={{
                        width: ratio(overview.conversations.dead, peakConvs),
                        background: 'var(--clay)',
                      }}
                    />
                  </div>
                  <span className="bars-value">{overview.conversations.dead}</span>
                </li>
              </ul>
            </section>
          </>
        )}

        {communities && (
          <section className="outcomes-section">
            <h2>Communities</h2>
            <ul className="bars">
              {communities.map((c) => (
                <li key={c.id}>
                  <span className="bars-label">{c.name}</span>
                  <span className="bars-meta">
                    {c.activeMembers} members · {c.activeConversations} convos · {c.messagesLast7d}{' '}
                    msgs/7d
                  </span>
                  <span className="bars-value">{c.status}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {readiness && (
          <section className="outcomes-section">
            <h2>
              System readiness{' '}
              <span
                className="bars-meta"
                style={{ color: readiness.ready ? 'var(--green)' : 'var(--clay)' }}
              >
                {readiness.ready ? '✓ all green' : '⚠ check items below'}
              </span>
            </h2>
            <ul className="bars">
              {readiness.checks.map((check) => {
                const colour =
                  check.status === 'ok'
                    ? 'var(--green)'
                    : check.status === 'warn'
                      ? 'var(--clay)'
                      : 'var(--clay)';
                const glyph = check.status === 'ok' ? '✓' : check.status === 'warn' ? '!' : '✗';
                return (
                  <li key={check.name}>
                    <span className="bars-label">
                      <span style={{ color: colour, fontWeight: 600 }}>{glyph}</span> {check.name}
                    </span>
                    <span className="bars-meta">{check.detail}</span>
                    <span className="bars-value">{check.status}</span>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {modStats && (
          <section className="outcomes-section">
            <h2>
              Moderation analytics{' '}
              <span className="bars-meta">Queue depth + 7-day resolution latency</span>
            </h2>
            <ul className="bars">
              <li>
                <span className="bars-label">Open in queue</span>
                <span className="bars-meta">Untouched moderation_event rows</span>
                <span
                  className="bars-value"
                  style={{ color: modStats.queue.open > 10 ? 'var(--clay)' : 'var(--ink)' }}
                >
                  {modStats.queue.open}
                </span>
              </li>
              <li>
                <span className="bars-label">Reviewing</span>
                <span className="bars-meta">A moderator has the item open</span>
                <span className="bars-value">{modStats.queue.reviewing}</span>
              </li>
              <li>
                <span className="bars-label">Resolved (7d)</span>
                <span className="bars-meta">
                  p50 {fmtDuration(modStats.last7d.p50Seconds)} · p95{' '}
                  {fmtDuration(modStats.last7d.p95Seconds)}
                </span>
                <span className="bars-value">{modStats.last7d.resolved}</span>
              </li>
              <li>
                <span className="bars-label">Crisis events (7d)</span>
                <span className="bars-meta">self_harm category from cascade or report</span>
                <span
                  className="bars-value"
                  style={{ color: modStats.last7d.crisis > 0 ? 'var(--clay)' : 'var(--ink)' }}
                >
                  {modStats.last7d.crisis}
                </span>
              </li>
              <li>
                <span className="bars-label">Opus auto-resolved (7d)</span>
                <span className="bars-meta">
                  Second-pass reviewer closed it without human touch
                </span>
                <span
                  className="bars-value"
                  style={{
                    color: modStats.last7d.opusResolved > 0 ? 'var(--green)' : 'var(--ink)',
                  }}
                >
                  {modStats.last7d.opusResolved}
                </span>
              </li>
              <li>
                <span className="bars-label">Opus escalated (7d)</span>
                <span className="bars-meta">Kicked back to the human queue with reasoning</span>
                <span className="bars-value">{modStats.last7d.opusEscalated}</span>
              </li>
              <li>
                <span className="bars-label">Severity mix (7d)</span>
                <span className="bars-meta">
                  low {modStats.last7d.severity.low} · med {modStats.last7d.severity.medium} · high{' '}
                  {modStats.last7d.severity.high} · crit {modStats.last7d.severity.critical}
                </span>
                <span className="bars-value">
                  {modStats.last7d.severity.low +
                    modStats.last7d.severity.medium +
                    modStats.last7d.severity.high +
                    modStats.last7d.severity.critical}
                </span>
              </li>
            </ul>
          </section>
        )}

        <section className="outcomes-section">
          <h2>
            Onboard a community{' '}
            <button
              type="button"
              className="link"
              onClick={() => {
                setNewCommOpen((v) => !v);
                setNewCommMsg(null);
              }}
            >
              {newCommOpen ? 'Cancel' : 'New community →'}
            </button>
          </h2>
          {!newCommOpen && (
            <p className="muted">
              Stand up a new university, employer, or health-plan tenant. One row, no engineer
              needed. Email-domain verification + the binding safety constraints all kick in
              automatically.
            </p>
          )}
          {newCommOpen && (
            <div className="stack" style={{ gap: '0.75rem', marginTop: '0.75rem' }}>
              <label>
                Name
                <input
                  value={newComm.name}
                  onChange={(e) => setNewComm({ ...newComm, name: e.target.value })}
                  placeholder="Stanford Wellness Pilot"
                />
              </label>
              <label>
                Slug (lowercase, hyphenated)
                <input
                  value={newComm.slug}
                  onChange={(e) =>
                    setNewComm({
                      ...newComm,
                      slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'),
                    })
                  }
                  placeholder="stanford-wellness"
                />
              </label>
              <label>
                Type
                <select
                  value={newComm.type}
                  onChange={(e) =>
                    setNewComm({ ...newComm, type: e.target.value as CreateCommunityInput['type'] })
                  }
                >
                  <option value="university">university</option>
                  <option value="employer">employer</option>
                  <option value="health_plan">health_plan</option>
                  <option value="nonprofit">nonprofit</option>
                </select>
              </label>
              <label>
                Email domains (comma or space-separated)
                <input
                  value={newCommDomainsRaw}
                  onChange={(e) => setNewCommDomainsRaw(e.target.value)}
                  placeholder="stanford.edu, alumni.stanford.edu"
                />
              </label>
              <label>
                Crisis escalation — who to contact for a crisis event
                <input
                  value={newComm.crisisEscalationName}
                  onChange={(e) => setNewComm({ ...newComm, crisisEscalationName: e.target.value })}
                  placeholder="Stanford Counseling & Psychological Services"
                />
              </label>
              <label>
                Crisis escalation contact (email or phone)
                <input
                  value={newComm.crisisEscalationContact}
                  onChange={(e) =>
                    setNewComm({ ...newComm, crisisEscalationContact: e.target.value })
                  }
                  placeholder="caps-oncall@stanford.edu"
                />
              </label>
              <label>
                Crisis escalation type
                <select
                  value={newComm.crisisEscalationType}
                  onChange={(e) =>
                    setNewComm({
                      ...newComm,
                      crisisEscalationType: e.target
                        .value as CreateCommunityInput['crisisEscalationType'],
                    })
                  }
                >
                  <option value="campus_office">campus_office</option>
                  <option value="eap">eap</option>
                  <option value="hotline">hotline</option>
                  <option value="other">other</option>
                </select>
              </label>
              <label>
                Resource note (optional, surfaced to crisis-flagged users)
                <textarea
                  value={newComm.crisisResourceNote ?? ''}
                  rows={2}
                  onChange={(e) => setNewComm({ ...newComm, crisisResourceNote: e.target.value })}
                  placeholder="Stanford CAPS is open 24/7 at (650) 723-3785."
                />
              </label>
              <div className="modal-actions">
                <button
                  type="button"
                  disabled={newCommBusy}
                  onClick={() => void submitNewCommunity()}
                >
                  {newCommBusy ? 'Creating…' : 'Create community'}
                </button>
                <button
                  type="button"
                  className="secondary"
                  disabled={newCommBusy}
                  onClick={() => setNewCommOpen(false)}
                >
                  Cancel
                </button>
              </div>
              {newCommMsg && <p className="muted">{newCommMsg}</p>}
            </div>
          )}
        </section>

        {communities && communities.length > 0 && (
          <section className="outcomes-section">
            <h2>
              Concierge match{' '}
              <span className="bars-meta">
                Hand-pair two unmatched members. Useful in the first weeks of a community before
                signups have critical mass.
              </span>
            </h2>
            <div className="stack" style={{ maxWidth: '36rem' }}>
              <label>
                Community
                <select
                  value={conciergeCommunityId}
                  onChange={(event) => void loadUnmatched(event.target.value)}
                >
                  <option value="">Pick a community…</option>
                  {communities.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
              {unmatched !== null && (
                <>
                  {unmatched.length === 0 ? (
                    <p className="muted">No unmatched members in this community.</p>
                  ) : (
                    <>
                      <label>
                        Member A
                        <select
                          value={pickedA}
                          onChange={(event) => setPickedA(event.target.value)}
                        >
                          <option value="">Pick a member…</option>
                          {unmatched
                            .filter((m) => m.membershipId !== pickedB)
                            .map((m) => (
                              <option key={m.membershipId} value={m.membershipId}>
                                {m.nickname}
                                {m.tags.length ? ` — ${m.tags.join(', ')}` : ''}
                              </option>
                            ))}
                        </select>
                      </label>
                      <label>
                        Member B
                        <select
                          value={pickedB}
                          onChange={(event) => setPickedB(event.target.value)}
                        >
                          <option value="">Pick a member…</option>
                          {unmatched
                            .filter((m) => m.membershipId !== pickedA)
                            .map((m) => (
                              <option key={m.membershipId} value={m.membershipId}>
                                {m.nickname}
                                {m.tags.length ? ` — ${m.tags.join(', ')}` : ''}
                              </option>
                            ))}
                        </select>
                      </label>
                      <button
                        type="button"
                        disabled={!pickedA || !pickedB || pickedA === pickedB || conciergeBusy}
                        onClick={() => void pairThem()}
                      >
                        {conciergeBusy ? 'Pairing…' : 'Match these two'}
                      </button>
                    </>
                  )}
                  {conciergeMsg && <p className="muted">{conciergeMsg}</p>}
                </>
              )}
            </div>
          </section>
        )}

        {demand && demand.length > 0 && (
          <section className="outcomes-section">
            <h2>
              Demand{' '}
              <span className="bars-meta">
                Who's asking about us — the partnerships prospect list, grouped by domain.
              </span>
            </h2>
            <ul className="bars">
              {demand.map((row) => {
                const label = row.institutionName ?? row.emailDomain;
                const meta = [row.institutionType, row.emailDomain, row.anyContact]
                  .filter(Boolean)
                  .join(' · ');
                return (
                  <li key={`${row.emailDomain}-${row.institutionName ?? ''}`}>
                    <span className="bars-label">{label}</span>
                    <span className="bars-meta">{meta}</span>
                    <span className="bars-value">{row.count}</span>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
        {demand && demand.length === 0 && (
          <section className="outcomes-section">
            <h2>Demand</h2>
            <p className="muted">
              No outreach leads yet. When someone tries to sign up with an unsupported email, the
              UnsupportedCommunity screen captures their interest here.
            </p>
          </section>
        )}
      </div>
    </main>
  );
}
